import React, { useCallback, useMemo, useRef, useState } from 'react';
import { SourceItem } from "../../shared/models/source-item.model";
import { useSiteContext } from "../../state/SiteContext";
import { FilterActionName, useFilterAction, useFilterState, useLangFilter } from "../../state/FilterContext";
import { TextExtractor, findFirstComponentOfType } from "../internal/TextExtractor";
import { ArrayProvider } from "../../state/ArrayContext";
import { BaseSelector, collectProps } from "../internal/BaseSelector";
import { useLocation } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { LanguageDefinitions } from '../source-components/array-sources/LanguageDefinitions';
import { applyDefaults, getStateAsLocationQuery } from '../../state/state-url-management';
import { useFilterStateController } from '../../state/filter-state-controller.context';
import { buildFilterRedirectNavigateTo, hasFilterRedirectNavigateIntent } from '../../shared/utils/filter-redirect-url.utils';
import { useToggleSwitchController } from '../../state/toggle-switch-controller.context';
import { activateToggleSwitchByName } from './toggle-switch-activation.utils';

interface LanguageSelectorProps {
  optionlabeltemplate: string,
  redirect?: string,
  redirectLayout?: string,
  activateToggleSwitch?: string,
  children: React.ReactNode,
}
/***
 * A selector component that allows the user to select a value from a list of values.
 * The list of values is loaded using an ArraySource specification.
 */
export const LanguageSelector = React.memo(function LanguageSelector({ optionlabeltemplate, redirect, redirectLayout = "", activateToggleSwitch, children }: LanguageSelectorProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const setLang = useFilterAction(FilterActionName.UPDATE_LANG);
  const lang = useLangFilter();
  const filterState = useFilterState();
  const controller = useFilterStateController();
  const redirectWarningLoggedRef = useRef(false);
  const toggleSwitchController = useToggleSwitchController();

  // Get the label from the label child
  const labelChild = useMemo(() => findFirstComponentOfType(children, "arg-label"), [children]);
  const [labelValue, setLabelValue] = useState<string | null>(null);

  const { siteState } = useSiteContext();

  // Get the autoCompleteProps from the autoCompleteProps child
  const autoCompletePropsChild = useMemo(() => findFirstComponentOfType(children, "arg-autocompleteprops"), [children]);
  const autoCompleteProps = useMemo(() => collectProps(autoCompletePropsChild), [autoCompletePropsChild]);

  // Get the textfieldProps from the textfieldProps child
  const textfieldPropsChild = useMemo(() => findFirstComponentOfType(children, "arg-textfieldprops"), [children]);
  const textfieldProps = useMemo(() => collectProps(textfieldPropsChild), [textfieldPropsChild]);


  // Get the valueset array
  const [valueSetArray, setValueSetArray] = useState<Array<SourceItem>>([]);
  const valuesetChild = useMemo(() => findFirstComponentOfType(children, "arg-valueset"), [children]);

  const initialValuesArray = useMemo(() => lang ? [lang] : [], [lang]);

  const onChangeFunc = useCallback((values: Array<SourceItem>) => {
    if (controller) {
      if (hasFilterRedirectNavigateIntent(redirect, redirectLayout) && !redirectWarningLoggedRef.current) {
        console.warn('[FilterStateController] Ignoring redirect and redirectLayout behavior for "LanguageSelector" in controller scope.');
        redirectWarningLoggedRef.current = true;
      }
      controller.requestChange((stateSnapshot) => {
        const nextState = { ...stateSnapshot };
        nextState.lang = values.length ? values[0].value as string || undefined : undefined;
        return nextState;
      }, { source: "languageselector:lang" });
      activateToggleSwitchByName(activateToggleSwitch, toggleSwitchController, "LanguageSelector");
      return;
    }

    const intentsRedirectNavigate = hasFilterRedirectNavigateIntent(redirect, redirectLayout);
    let redirectToPath: string | null = null;
    if (intentsRedirectNavigate) {
      const filterStateCopy = { ...filterState };
      filterStateCopy.lang = values.length ? values[0].value as string || undefined : undefined;
      if (siteState?.site) {
        applyDefaults(filterStateCopy, siteState?.site);
      }
      const searchParams = getStateAsLocationQuery(filterStateCopy);
      redirectToPath = buildFilterRedirectNavigateTo({
        effectiveSitePath: siteState.sitePath ?? "",
        redirect,
        redirectLayout,
        currentPathname: location.pathname,
        searchParamsString: searchParams.toString(),
        locationHash: location.hash,
        activeToggles: activateToggleSwitch,
      });
    }
    if (values.length) {
      // Langs filter only has one value
      setLang(values[0].value);
    }
    else {
      setLang("");
    }
    activateToggleSwitchByName(activateToggleSwitch, toggleSwitchController, "LanguageSelector");
    if (redirectToPath) {
      navigate(redirectToPath, { replace: true });
    }
  }, [
    activateToggleSwitch,
    controller,
    setLang,
    redirect,
    redirectLayout,
    siteState.sitePath,
    siteState.site,
    filterState,
    navigate,
    location.hash,
    location.pathname,
    toggleSwitchController,
  ]);

  return <>
    <BaseSelector
      label={labelValue || ""}
      valueset={valueSetArray}
      initialValues={initialValuesArray}
      optionlabeltemplate={optionlabeltemplate}
      autocompleteprops={autoCompleteProps}
      textfieldprops={textfieldProps}
      multiple={false}
      onChange={onChangeFunc}
    />

    <ArrayProvider setArray={setValueSetArray}>
      {valuesetChild?.props.children || <LanguageDefinitions />}
    </ArrayProvider>
    <TextExtractor onExtractedText={setLabelValue}>
      {labelChild?.props.children || "Language"}
    </TextExtractor>
  </>
});